import { z } from 'zod';
import { FC } from 'react';
import { styled } from '@mui/material';
import { AppSelectBox } from 'components/FormElements';
import FormLabel from 'components/FormElements/FormLabel';
import { Column, Row } from 'components/Layout';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { investorAccessGrants as rules } from 'further-ui/validations';
import { getInvestorAccessPermissionGroupOptions } from 'further-ui/utils';
import {
  InvestorAccessPermissionGroup,
  InvestorAccessPermissionGroupKey,
  InvestorAccessPermission,
} from 'further-types/further-user';
import FirmAssociatedEmailsSelector from 'components/FirmAssociatedEmailsSelector';
import trpcApi from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import Button from 'components/Button';

const FormRow = styled(Row)({
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});
const FormColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'flex',
})<{ flex?: number }>(({ flex }) => ({
  flex: flex ?? 1,
}));
const SaveButton = styled(Button)({
  marginTop: '1.1rem',
});

type Props = {
  investorId: string;
  firmId: string;
  emailsOfExistingUsers: Array<string>;
  closePanel: () => void;
};

type FormValues = {
  investorId: string;
  email: string;
  copyIntoSystemEmails: boolean;
  role: InvestorAccessPermissionGroupKey;
  accessPermissions: Array<InvestorAccessPermission>;
  firmId: string;
};

const AddNewInvestorAccessGrant: FC<Props> = ({
  investorId,
  firmId,
  emailsOfExistingUsers,
  closePanel,
}) => {
  const notification = useNotification();

  const create = trpcApi.investorAccessGrant.create.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Investor access grant created',
      onSuccess: () => {
        closePanel();
      },
    }),
  );

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(
      rules.CreateAccessGrantInput.omit({ accessPermissions: true }).extend({
        role: z.nativeEnum(InvestorAccessPermissionGroupKey, {
          errorMap: () => ({ message: 'Please select a role.' }),
        }),
        email: z
          .string()
          .email('Please enter a valid email address')
          .refine((val) => !emailsOfExistingUsers.includes(val), {
            message: 'This email already has access.',
          }),
      }),
    ),
    defaultValues: {
      investorId,
      email: '',
      copyIntoSystemEmails: true,
      role: InvestorAccessPermissionGroupKey.Full,
      firmId,
    },
  });

  const onSubmit = (values: FormValues) => {
    const accessPermissions = InvestorAccessPermissionGroup[values.role];

    if (!accessPermissions || accessPermissions.length === 0) {
      console.error('No access permissions found for role', values.role);
      notification.error('No access permissions found.');
      return;
    }

    create.mutate({
      investorId,
      email: values.email,
      copyIntoSystemEmails: values.copyIntoSystemEmails,
      firmId,
      accessPermissions,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormRow spacing="md">
        <FormColumn flex={2}>
          <FormLabel
            title="Email address"
            tooltipText="Enter a new email address in the box, or select from this list of existing emails that are linked to your investors, advisers or organisation users."
          />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <FirmAssociatedEmailsSelector
                firmId={firmId}
                excludeEmails={emailsOfExistingUsers}
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error}
              />
            )}
          />
        </FormColumn>

        <FormColumn>
          <FormLabel title="Copy user into emails?" />
          <Controller
            name="copyIntoSystemEmails"
            control={control}
            render={({ field, fieldState }) => (
              <AppSelectBox
                data={[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ]}
                labelKey="label"
                valueKey="value"
                value={field.value}
                variant="outlined"
                onChange={(event: any) => field.onChange(event.target.value)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormColumn>

        <FormColumn>
          <FormLabel title="Permissions" />
          <Controller
            name="role"
            control={control}
            render={({ field, fieldState }) => (
              <AppSelectBox
                data={getInvestorAccessPermissionGroupOptions()}
                labelKey="label"
                valueKey="value"
                value={field.value}
                variant="outlined"
                onChange={(event: any) => field.onChange(event.target.value)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormColumn>

        <SaveButton
          color="primary"
          variant="contained"
          type="submit"
          loading={create.isPending}
          disabled={create.isPending}
        >
          Save
        </SaveButton>
      </FormRow>
    </form>
  );
};

export default AddNewInvestorAccessGrant;
